import { Link, useNavigate } from 'react-router-dom'
import { Home, Store, ShoppingCart, User, ChefHat, Truck, Building2, ArrowLeft, LogOut, TrendingUp, ClipboardList, Wallet } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useCart } from '../contexts/CartContext'
import { useStore } from '../contexts/StoreContext'
import ProfilePhoto from '../components/ProfilePhoto'

const MenuScreen = () => {
  const navigate = useNavigate()
  const { user, logout, isStoreOwner, isDispatcher, isChef, isDelivery } = useAuth()
  const { itemCount } = useCart()
  const { hasMultipleStores } = useStore()

  const handleLogout = () => {
    if (window.confirm('¿Estás seguro de que quieres cerrar sesión?')) {
      logout()
      navigate('/')
    }
  }

  const getAllMenuItems = () => {
    if (!user) {
      return [
        { path: '/', label: 'Inicio', icon: Home, description: 'Página principal', color: 'from-blue-500 to-blue-600' },
        { path: '/stores', label: 'Negocios', icon: Store, description: 'Explorar tiendas', color: 'from-green-500 to-green-600' },
        { path: '/login', label: 'Iniciar Sesión', icon: User, description: 'Acceder a tu cuenta', color: 'from-purple-500 to-purple-600' },
      ]
    }

    if (isStoreOwner) {
      const items = [
        { path: '/store-owner/dashboard', label: 'Panel de Control', icon: Building2, description: 'Panel de control', color: 'from-primary-500 to-primary-600' },
        { path: '/stores', label: 'Colmados', icon: Store, description: 'Ver todas las tiendas', color: 'from-green-500 to-green-600' },
        { path: '/profile', label: 'Mi Perfil', icon: User, description: 'Configuración de cuenta', color: 'from-purple-500 to-purple-600' },
      ]

      // Agregar dashboard de rendimiento si tiene múltiples negocios
      if (hasMultipleStores) {
        items.splice(1, 0, { path: '/store/dashboard', label: 'Rendimiento', icon: TrendingUp, description: 'Rendimiento de todos los negocios', color: 'from-orange-500 to-orange-600' })
      }

      return items
    }

    if (isDispatcher) {
      return [
        { path: '/dispatcher', label: 'Panel de Despacho', icon: Building2, description: 'Gestión de entregas', color: 'from-blue-500 to-blue-600' },
        { path: '/stores', label: 'Colmados', icon: Store, description: 'Tiendas disponibles', color: 'from-green-500 to-green-600' },
        { path: '/profile', label: 'Mi Perfil', icon: User, description: 'Información personal', color: 'from-purple-500 to-purple-600' },
      ]
    }

    if (isChef) {
      return [
        { path: '/store/kitchen', label: 'Cocina', icon: ChefHat, description: 'Pedidos pendientes', color: 'from-orange-500 to-orange-600' },
        { path: '/stores', label: 'Colmados', icon: Store, description: 'Tiendas disponibles', color: 'from-green-500 to-green-600' },
        { path: '/profile', label: 'Mi Perfil', icon: User, description: 'Información personal', color: 'from-purple-500 to-purple-600' },
      ]
    }

    if (isDelivery) {
      const deliveryPath = user?.storeId ? `/store/${user.storeId}/delivery` : '/store/delivery'
      return [
        { path: deliveryPath, label: 'Entregas', icon: Truck, description: 'Pedidos en ruta', color: 'from-red-500 to-red-600' },
        { path: '/store/delivery/wallet', label: 'Mi Cartera', icon: Wallet, description: 'Gestionar dinero', color: 'from-green-500 to-green-600' },
        { path: '/profile', label: 'Mi Perfil', icon: User, description: 'Información personal', color: 'from-purple-500 to-purple-600' },
      ]
    }

    // Cliente
    return [
      { path: '/', label: 'Inicio', icon: Home, description: 'Página principal', color: 'from-blue-500 to-blue-600' },
      { path: '/stores', label: 'Colmados', icon: Store, description: 'Explorar tiendas', color: 'from-green-500 to-green-600' },
      { path: '/cart', label: 'Mi Carrito', icon: ShoppingCart, description: 'Productos seleccionados', color: 'from-yellow-500 to-yellow-600', badge: itemCount },
      { path: '/orders', label: 'Mis Pedidos', icon: ClipboardList, description: 'Historial de compras', color: 'from-indigo-500 to-indigo-600' },
      { path: '/profile', label: 'Mi Perfil', icon: User, description: 'Configuración de cuenta', color: 'from-purple-500 to-purple-600' },
    ]
  }

  const menuItems = getAllMenuItems()

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50 pb-20 sm:pb-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-2xl shadow-sm border-b border-gray-200/60 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 p-2 rounded-xl text-gray-600 hover:text-primary-600 hover:bg-gray-50 transition-all duration-200 active:scale-95"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline font-semibold">Volver</span>
            </button>

            <h1 className="text-lg sm:text-xl font-bold text-gray-900">Menú</h1>

            <div className="w-10"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Menu Items Grid - Compacto y variado */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {menuItems.map((item) => {
            const Icon = item.icon
            const colorClass = item.color || 'from-gray-500 to-gray-600'
            return (
              <Link
                key={item.path}
                to={item.path}
                className="group relative bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-5 shadow-lg border-2 border-gray-200/60 hover:shadow-2xl hover:shadow-gray-900/10 transition-all duration-300 hover:-translate-y-1 active:scale-95 overflow-hidden"
              >
                {/* Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${colorClass} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                
                {/* Content */}
                <div className="relative flex flex-col items-center text-center">
                  {/* Icon Container */}
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${colorClass} rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg mb-3 group-hover:scale-110 transition-transform duration-300`}>
                    {item.path === '/profile' && user ? (
                      <ProfilePhoto 
                        photoUrl={user.profilePhotoUrl || (user as any).profile_photo_url} 
                        size="md" 
                        showBorder={false}
                        className="group-hover:scale-110 transition-transform duration-300"
                      />
                    ) : (
                      <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                    )}
                  </div>

                  {/* Label */}
                  <h3 className="text-xs sm:text-sm font-bold text-gray-900 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {item.label}
                      </h3>
                  
                  {/* Description - Solo en desktop */}
                  <p className="hidden sm:block text-[10px] sm:text-xs text-gray-500 line-clamp-2 mt-1">
                    {item.description}
                  </p>

                  {/* Badge */}
                      {item.badge && item.badge > 0 && (
                    <span className="absolute top-2 right-2 bg-red-500 text-white text-[10px] sm:text-xs rounded-full min-w-[18px] h-[18px] sm:min-w-[20px] sm:h-5 flex items-center justify-center font-bold shadow-md px-1.5 border-2 border-white">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                </div>
              </Link>
            )
          })}
        </div>

        {/* User Info Card - Mejorado */}
        {user && (
          <>
            <div className="bg-gradient-to-br from-white to-gray-50/50 rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 p-4 sm:p-6 mb-4">
              <div className="flex items-center space-x-4">
                <ProfilePhoto 
                  photoUrl={user.profilePhotoUrl} 
                  size="lg" 
                  showBorder={true}
                  className="ring-2 ring-primary-200"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{user.name}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 capitalize mt-0.5">
                    {user.type === 'store_owner' ? 'Dueño de Negocio' :
                     user.type === 'store_admin' ? 'Administrador de Negocio' :
                     user.type === 'customer' ? 'Cliente' :
                     user.type === 'dispatcher' ? 'Despachador' :
                     user.type === 'chef' ? 'Cocinero' :
                     user.type === 'delivery' ? 'Repartidor' :
                     user.type === 'platform_admin' ? 'Administrador de Plataforma' :
                     (user.type ? String(user.type).replace('_', ' ') : 'Usuario')}
                  </p>
                </div>
              </div>
            </div>

            {/* Logout Button - Mejorado */}
              <button
                onClick={handleLogout}
              className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-2xl sm:rounded-3xl p-4 sm:p-5 shadow-lg hover:shadow-2xl transition-all duration-300 active:scale-95 group"
              >
              <div className="flex items-center justify-center space-x-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-300">
                  <LogOut className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                <div className="text-left">
                  <p className="text-sm sm:text-base font-bold">Cerrar Sesión</p>
                  <p className="text-xs sm:text-sm opacity-90">Salir de tu cuenta</p>
                  </div>
                </div>
              </button>
          </>
        )}
      </div>
    </div>
  )
}

export default MenuScreen
